import { HTMLProps, MouseEventHandler, useCallback } from 'react';
import { twMerge } from 'tailwind-merge';
import { Color } from '@diritto/common/style/color';
import { CategoryType, CategoryTypeString } from '@diritto/common/content/constants';
import { useMenuRef } from '@/ui-hooks/useMenuRef';
import { CategoryFilterItem, CategoryFilterItemList } from '@/ui-components/common/CategoryFilterItemList';
import { ArrowIcon } from '@/icons/ArrowIcon';

type Modify<T, R> = Omit<T, keyof R> & R;

type CategoryFilterBoxProps = Modify<
  HTMLProps<HTMLInputElement>,
  {
    className?: string;
    items: CategoryFilterItem[];
    categoryType: CategoryType;
  }
>;

export const CategoryFilterBox = ({ className, items, categoryType }: CategoryFilterBoxProps) => {
  const { ref, isOpen, setOpen } = useMenuRef<HTMLDivElement>();

  const handleClick: MouseEventHandler<HTMLDivElement> = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setOpen(prev => !prev);
    },
    [setOpen],
  );

  const handleToggleMenu = useCallback(
    (value?: boolean) => {
      setOpen(prev => value ?? !prev);
    },
    [setOpen],
  );

  const handleItemSelect = useCallback(
    (item: CategoryFilterItem) => {
      item?.onClick?.();
      handleToggleMenu(false);
    },
    [handleToggleMenu],
  );

  return (
    <div ref={ref} className={twMerge('relative flex items-center justify-center h-6 cursor-pointer', className)}>
      <div onClick={handleClick} className="flex items-center justify-center gap-1">
        <p className="text-xxs font-medium text-gray-600">{CategoryTypeString[categoryType]}</p>
        <ArrowIcon color={Color.Gray600} direction={isOpen ? 'up' : 'down'} className="size-[14px]" />
      </div>
      {isOpen && (
        <CategoryFilterItemList
          items={items.map(item => ({
            ...item,
            onClick: () => handleItemSelect(item),
          }))}
        />
      )}
    </div>
  );
};
